import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiLoader, FiMapPin, FiMessageCircle, FiSend } from 'react-icons/fi';
import { cUser, cUserPosts, cUsers } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import BlockButton from '../../components/community/BlockButton';
import FollowButton from '../../components/community/FollowButton';
import ImageLightbox from '../../components/community/ImageLightbox';
import PostCard from '../../components/community/PostCard';
import UserAvatar from '../../components/community/UserAvatar';
import UserBadges from '../../components/community/UserBadges';
import { NAVY, BLUE, compactNumber } from '../../components/community/brand';

/** A member's public profile (cover, counts, follow / follow-back + articles). */
export default function CommunityUserPage() {
  const { username } = useParams();
  const { t } = useLanguage();
  const { user: me, requireAuth } = useCommunity();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);   // profile image modal

  useEffect(() => {
    let alive = true;
    setLoading(true);
    cUsers({ q: username, limit: 20 })
      .then((res) => {
        const found = (res?.items || []).find(
          (u) => (u.username || '').toLowerCase() === String(username).toLowerCase(),
        );
        if (!found) throw new Error('not found');
        return cUser(found.id);
      })
      .then((full) => {
        if (!alive) return null;
        setProfile(full);
        return cUserPosts(full.id, { limit: 10 });
      })
      .then((res) => { if (alive && res) setPosts(res.items || []); })
      .catch(() => { if (alive) setProfile(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [username]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-gray-500">
        <FiLoader className="w-5 h-5 animate-spin" /> {t('loading')}
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <p className="font-bold">{t('notFound404')}</p>
        <Link to="/" className="inline-flex items-center gap-2 mt-4 text-white px-4 py-2 rounded-full font-bold" style={{ background: NAVY }}>
          <FiArrowLeft /> {t('backToFeed')}
        </Link>
      </div>
    );
  }

  const isMe = me && me.id === profile.id;
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  return (
    <div className="mx-auto max-w-3xl px-3 lg:px-4 py-4 space-y-4">
      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        <div
          className="h-32 sm:h-40 bg-cover bg-center cursor-zoom-in"
          data-sfx="pop"
          role="button"
          tabIndex={0}
          onClick={() => setLightbox(profile.cover
            ? { src: profile.cover.startsWith('http') ? profile.cover : `${apiBase}${profile.cover}`, caption: `${profile.name || profile.username} · ${t('cover')}` }
            : null)}
          onKeyDown={() => setLightbox(profile.cover
            ? { src: profile.cover.startsWith('http') ? profile.cover : `${apiBase}${profile.cover}`, caption: `${profile.name || profile.username} · ${t('cover')}` }
            : null)}
          style={profile.cover
            ? { backgroundImage: `url(${profile.cover.startsWith('http') ? profile.cover : `${apiBase}${profile.cover}`})` }
            : { background: 'linear-gradient(135deg,#0B5FDB,#083E93)' }}
        />
        <div className="px-4 pb-4">
          <div className="flex items-end gap-3 -mt-10">
            {/* tap the profile photo to open it in a modal */}
            <button
              type="button"
              data-sfx="pop"
              onClick={() => setLightbox({
                src: profile.avatar
                  ? (profile.avatar.startsWith('http') ? profile.avatar : `${apiBase}${profile.avatar}`)
                  : '',
                caption: `${profile.name || profile.username} · @${profile.username}`,
              })}
              className="rounded-full focus:outline-none"
              title={t('viewPhoto')}
            >
              <UserAvatar user={profile} size={84} ring />
            </button>
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="font-extrabold text-lg truncate flex items-center gap-1.5">
                <span className="truncate">{profile.name || profile.username}</span>
                <UserBadges user={profile} size="lg" />
              </h1>
              <p className="text-xs text-gray-500">@{profile.username}</p>
            </div>
            <div className="flex items-center gap-2 pb-1 flex-wrap justify-end">
              {!isMe && <FollowButton user={profile} />}
              {!isMe && (
                <button
                  type="button"
                  onClick={() => {
                    if (!requireAuth(t('loginRequiredDesc'))) return;
                    navigate(`/messages/${profile.id}`);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white"
                  style={{ background: NAVY }}
                >
                  <FiMessageCircle className="w-3.5 h-3.5" /> {t('message')}
                </button>
              )}
              {/* គណនីដែលត្រូវបានប្លុក — block / unblock this member */}
              {!isMe && (
                <BlockButton
                  user={profile}
                  blocked={!!profile.blocked}
                  compact
                  onChanged={(nowBlocked) => setProfile((p) => (p ? { ...p, blocked: nowBlocked } : p))}
                />
              )}
              {isMe && (
                <Link to="/me" className="px-3 py-1.5 rounded-full text-xs font-bold text-white" style={{ background: BLUE }}>
                  {t('editProfile')}
                </Link>
              )}
            </div>
          </div>

          {profile.bio && (
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{profile.bio}</p>
          )}

          <div className="mt-3 flex items-center gap-4 text-sm">
            <span><b>{compactNumber(profile.posts_count)}</b> <span className="text-gray-500">{t('posts')}</span></span>
            <span><b>{compactNumber(profile.followers_count)}</b> <span className="text-gray-500">{t('followers')}</span></span>
            <span><b>{compactNumber(profile.following_count)}</b> <span className="text-gray-500">{t('following')}</span></span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {profile.city && (
              <span className="inline-flex items-center gap-1">
                <FiMapPin className="w-3.5 h-3.5" /> {profile.city}{profile.country ? `, ${profile.country}` : ''}
              </span>
            )}
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                {profile.website}
              </a>
            )}
            {profile.social?.telegram && (
              <a href={profile.social.telegram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold" style={{ color: '#229ED9' }}>
                <FiSend className="w-3.5 h-3.5" /> Telegram
              </a>
            )}
            {profile.social?.facebook && (
              <a href={profile.social.facebook} target="_blank" rel="noreferrer" className="font-semibold" style={{ color: '#1877F2' }}>
                Facebook
              </a>
            )}
          </div>
        </div>
      </section>

      <h2 className="font-extrabold text-sm px-1">{t('posts')}</h2>
      {posts.length === 0 ? (
        <p className="text-sm text-gray-400 px-1">{t('noPosts')}</p>
      ) : (
        posts.map((p) => (
          <PostCard
            key={p.id}
            post={p}
            onChanged={(updated) => setPosts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
            onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))}
          />
        ))
      )}

      {/* profile photo modal */}
      {lightbox && lightbox.src && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.caption || 'profile'}
          caption={lightbox.caption}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
